import { ConsoleLogger } from '@proposify/frontend-observability';
import Stack from '@mui/material/Stack';
import { Trans, useTranslation } from 'react-i18next';
import { navigate } from 'utils/navigate';
import { CommentedEventRow } from './event-row/CommentedEventRow';
import { Row } from './event-row/Row';
import type { TransformedNotificationItem } from './utils/EventTypes';
import { Event, eventMapping, getEventRow, isCommentedEvent, isViewedEvent } from './utils/EventTypes';
import { getTranslationValues, isEventTranslationValues } from './utils/getTranslationValues';
import { useGetPreviewByDocumentId } from 'pages/editor/useGetPreviewByDocumentId';
import { resolveNotificationDestination, shouldFetchNotificationDocument } from './utils/resolveNotificationDestination';

export const NotificationEventRow = ({
  event,
  handleMarkAsViewed,
  onClose,
}: {
  event: TransformedNotificationItem;
  handleMarkAsViewed: (notificationId: string) => void;
  onClose?: VoidFunction;
}) => {
  const { t } = useTranslation();
  const viewedEvent = isViewedEvent(event);
  const {
    data: document,
    isLoading,
    isError,
    error,
  } = useGetPreviewByDocumentId(event.documentId, {
    enabled: shouldFetchNotificationDocument(event),
  });

  const handleRowClick = () => {
    handleMarkAsViewed(event.notificationId);
    onClose?.();

    const destination = resolveNotificationDestination(event, document, { isLoading, isError });

    if (destination.type === 'skip') {
      if (destination.reason === 'noDocumentId') {
        ConsoleLogger.error('Cannot navigate: notification missing documentId', {
          notificationId: event.notificationId,
          type: event.type,
        });
      } else if (destination.reason === 'error') {
        ConsoleLogger.error('Failed to load document, skipping navigation', {
          documentId: event.documentId,
          error: error?.message,
        });
      } else if (destination.reason === 'noData') {
        ConsoleLogger.warn('Document query succeeded but no data returned, skipping navigation', {
          documentId: event.documentId,
        });
      }

      return;
    }

    navigate(`${destination.pathname}${destination.search}`);

    if (viewedEvent) {
      window.dispatchEvent(new CustomEvent('urlParamChanged', { detail: { key: 'insightsDocId' } }));
    }
  };

  if (!Object.prototype.hasOwnProperty.call(eventMapping, event.type)) {
    ConsoleLogger.error('Missing or unknown event type:', event.type);
    return null;
  }

  const eventRow = getEventRow(event.type, event);
  const translationValues = getTranslationValues(event, t);

  if (isCommentedEvent(event) && isEventTranslationValues(translationValues, Event.COMMENTED)) {
    const { author, documentName } = translationValues;
    return (
      <CommentedEventRow event={event} icon={eventRow.icon} handleClick={handleRowClick} translationValues={{ author, documentName }} />
    );
  }

  if (event.type === Event.VIEWED) {
    const key = event.viewerName ? 'viewed_event' : 'viewed_event_anonymous';
    return (
      <Row handleClick={handleRowClick} icon={eventRow.icon} datetime={event.createdAt} viewed={event.viewed}>
        <Stack direction="row" sx={{ whiteSpace: 'pre', fontSize: '16px', color: 'text.primary', flexWrap: 'wrap' }}>
          <Trans i18nKey={`notifications_feed.${key}`} values={translationValues} />
        </Stack>
      </Row>
    );
  }

  return (
    <Row handleClick={handleRowClick} icon={eventRow.icon} datetime={event.createdAt} viewed={event.viewed}>
      <Stack direction="row" sx={{ whiteSpace: 'pre', fontSize: '16px', color: 'text.primary', flexWrap: 'wrap' }}>
        <Trans i18nKey={`notifications_feed.${eventRow.key}`} values={translationValues} />
      </Stack>
    </Row>
  );
};

export default NotificationEventRow;
